import React from 'react'
import PropTypes from 'prop-types'

import Text from '../../template/components/Text.jsx'

import { useLazyLoad } from '_hooks'
import { PROPTYPES } from '../helpers/propTypes'

import './styles.scss'

const format = (data, mode) => {
  if (mode === 'single') {
    return { formatted: data, entries: null }
  }

  const formatted = data.map((d) => {
    const items = Object.fromEntries(
      d.entries.flatMap((entry) => [
        [entry.name, (entry.value / 100) * d.value],
        [entry.name + 'Distribution', entry.value],
      ])
    )
    return {
      name: d.name,
      value: d.value,
      ...items,
    }
  })

  const entries = [...new Set(data.flatMap((d) => d.entries.map((e) => e.name)))]

  return { formatted, entries }
}

const CustomizedLabel = (props) => {
  const { viewBox, value, fill } = props

  if (viewBox.height < 30) return null

  return (
    <svg {...viewBox} y={viewBox.y + viewBox.height / 2 - 12}>
      <Text
        value={value ? `${value.toString()}%` : '-'}
        fontSize={24}
        textAnchor="middle"
        fill={fill}
        width={viewBox.width}
        x={0}
        y={0}
      />
    </svg>
  )
}

const BarStackedCartesian = ({ data, width, height, legend, mode = 'stacked', colors, labels }) => {
  const [
    { BarChart, Bar, XAxis, YAxis, LabelList, CartesianGrid, Legend, ResponsiveContainer },
    loadingRecharts,
  ] = useLazyLoad(import('recharts'))

  const { formatted, entries } = format(data, mode)

  return (
    <If condition={!loadingRecharts}>
      <ResponsiveContainer width={width} height={height} className="bar-stacked-cartesian-recharts">
        <BarChart
          data={formatted}
          margin={{ top: 50, right: 120, left: 20, bottom: 120 }}
          barSize={80}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <YAxis
            dataKey="value"
            label={{
              value: labels.y,
              position: 'top',
              offset: 30,
            }}
            tickLine={false}
            allowDecimals={false}
            tickCount={10}
            tickMargin={12}
            unit="%"
          />
          <XAxis
            dataKey="name"
            type="category"
            label={{
              value: labels.x,
              position: 'right',
              offset: 0,
            }}
            tickLine={false}
            tickMargin={12}
          />
          {entries ? (
            entries.map((entry, index) => (
              <Bar
                key={entry}
                dataKey={entry}
                fill={colors[index]}
                stackId="a"
                isAnimationActive={false}
              >
                <LabelList
                  dataKey={`${entry}Distribution`}
                  position="inside"
                  fill="white"
                  content={<CustomizedLabel />}
                />
              </Bar>
            ))
          ) : (
            <Bar dataKey="value" fill="#228353" isAnimationActive={false}>
              <LabelList position="inside" fill="white" content={<CustomizedLabel />} />
            </Bar>
          )}
          {entries && (
            <Legend
              layout={legend.layout}
              align={legend.align}
              verticalAlign={legend.verticalAlign}
              iconType={legend.iconType}
              iconSize={legend.iconSize}
              formatter={(name) => name.charAt(0).toUpperCase() + name.slice(1)}
              wrapperStyle={{
                // border: '2px solid #EBEBEB',
                borderRadius: '16px',
                margin: '0 auto 0',
                padding: '12px',
                position: 'relative',
                left: 0,
                bottom: 0,
                transform: 'translateY(-100%)',
                fontSize: '28px',
                ...legend.wrapperStyle,
              }}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </If>
  )
}

BarStackedCartesian.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      entries: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          value: PropTypes.number.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  labels: PropTypes.shape({
    x: PropTypes.string.isRequired,
    y: PropTypes.string.isRequired,
  }).isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  legend: PROPTYPES.LEGEND,
  mode: PropTypes.oneOf(['single', 'stacked']),
}

BarStackedCartesian.defaultProps = {
  width: 640,
  height: 230,
  legend: {
    layout: 'horizontal',
    align: 'center',
    verticalAlign: 'bottom',
    iconType: 'circle',
    iconSize: 40,
    wrapperStyle: {},
  },
  mode: 'stacked',
}

export default BarStackedCartesian
